import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FetchFeedParams, fetchFeed } from "../services/feedService";
import { FeedFilters, FeedItem } from "../types/feed";

interface UseFeedOptions {
  pageSize?: number;
}

interface UseFeedResult {
  items: FeedItem[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  refresh: (filters: FeedFilters) => void;
  filters: FeedFilters;
}

function hasActor(item: FeedItem): boolean {
  if (!Array.isArray(item.actors) || item.actors.length === 0) {
    return false;
  }

  return item.actors.some((actor) => {
    const uid = typeof actor.uid === "string" ? actor.uid.trim() : "";
    const name = typeof actor.name === "string" ? actor.name.trim() : "";
    return uid.length > 0 || name.length > 0;
  });
}

function shouldRenderFeedItem(item: FeedItem): boolean {
  if (item.kind !== "event") {
    return true;
  }

  return hasActor(item);
}

export default function useFeed(initialFilters: FeedFilters = {}, options: UseFeedOptions = {}): UseFeedResult {
  const pageSize = options.pageSize ?? 50;
  const [items, setItems] = useState<FeedItem[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FeedFilters>(initialFilters);

  const filtersRef = useRef<FeedFilters>(initialFilters);
  const cursorRef = useRef<string | null>(null);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const inflightCursorRef = useRef<string | null>(null);
  const pendingLoadMoreRef = useRef(false);

  const requestFeed = useCallback(
    async (params: FetchFeedParams) => {
      const attemptedCursor = params.cursor ?? null;
      loadingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        inflightCursorRef.current = attemptedCursor ?? "__initial__";
        const response = await fetchFeed({ ...params, limit: params.limit ?? pageSize });
        const filteredItems = response.items.filter(shouldRenderFeedItem);
        setItems((prev) => (attemptedCursor ? [...prev, ...filteredItems] : filteredItems));

        const nextCursor = response.next_cursor ?? null;
        cursorRef.current = nextCursor;

        const more = Boolean(nextCursor);
        hasMoreRef.current = more;
        setHasMore(more);
      } catch (err) {
        if (attemptedCursor) {
          cursorRef.current = attemptedCursor;
        }

        const message = err instanceof Error ? err.message : "Unexpected error";
        setError(message);
      } finally {
        inflightCursorRef.current = null;
        loadingRef.current = false;
        setLoading(false);

        if (pendingLoadMoreRef.current) {
          pendingLoadMoreRef.current = false;
          if (hasMoreRef.current && cursorRef.current) {
            void requestFeed({ ...filtersRef.current, limit: pageSize, cursor: cursorRef.current });
          }
        }
      }
    },
    [pageSize]
  );

  const loadMore = useCallback(() => {
    if (!hasMoreRef.current) {
      return;
    }

    if (loadingRef.current || inflightCursorRef.current !== null) {
      pendingLoadMoreRef.current = true;
      return;
    }

    const cursorToLoad = cursorRef.current;
    if (!cursorToLoad) {
      return;
    }

    void requestFeed({ ...filtersRef.current, limit: pageSize, cursor: cursorToLoad });
  }, [pageSize, requestFeed]);

  const refresh = useCallback(
    (nextFilters: FeedFilters) => {
      filtersRef.current = nextFilters;
      cursorRef.current = null;
      hasMoreRef.current = true;

      setFilters(nextFilters);
      setHasMore(true);
      setItems([]);

      void requestFeed({ ...nextFilters, limit: pageSize, cursor: null });
    },
    [pageSize, requestFeed]
  );

  useEffect(() => {
    cursorRef.current = null;
    hasMoreRef.current = true;

    void requestFeed({ ...filtersRef.current, limit: pageSize, cursor: null });
  }, [pageSize, requestFeed]);

  return useMemo(
    () => ({ items, loading, error, hasMore, loadMore, refresh, filters }),
    [error, filters, hasMore, items, loadMore, loading, refresh]
  );
}
